import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'

const PRINCIPLES = [
    {
        title: 'Depth, not decoration',
        body: 'Every card is a real object at a real distance. Size comes purely from perspective, so a photograph genuinely travels toward you through the volume rather than being scaled up in place.'
    },
    {
        title: 'Sparse on purpose',
        body: 'Only a handful of frames occupy the stage at once. The field stays legible, each image gets its own region of the frame, and nothing ever stacks or clumps at the centre.'
    },
    {
        title: 'Fast, slow, fast',
        body: 'Cards rush in from the deep, ease through the readable middle where you can actually look at them, then rush out past the lens. The dwell is the whole point.'
    },
    {
        title: 'It never repeats',
        body: 'A recycled card draws a fresh photograph and a fresh anchor from the cells nothing else is using, so the same image never returns to the same place twice.'
    }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About'
                description='Fathom is a photographic flythrough: a deep volume of suspended cards, drifting forward out of the dark.'
            />

            <div className='min-h-dvh bg-stage px-5 pb-28 pt-32 sm:px-9 sm:pt-40'>
                <div className='mx-auto max-w-5xl'>
                    <span className='font-mono text-[0.7rem] uppercase tracking-[0.16em] text-white/50'>
                        N° 02 — About
                    </span>

                    <h1 className='mt-8 font-display text-[clamp(2.4rem,8vw,6rem)] uppercase leading-[0.98] tracking-[0.04em] text-white/90'>
                        Stories
                        <br />
                        In Motion
                    </h1>

                    <div className='mt-14 grid gap-12 border-t border-white/10 pt-12 md:grid-cols-[1fr_1.2fr] md:gap-20'>
                        <p className='font-serif text-[clamp(1.4rem,2.6vw,2rem)] leading-[1.2] text-paper'>
                            An endless forward approach on a pure-black stage, built to be
                            wandered rather than scrolled through.
                        </p>

                        <div className='space-y-6 text-[0.98rem] leading-[1.6] text-paper/70'>
                            <p>
                                The camera dollies through a deep volume of suspended, two-sided
                                photo cards scattered without order. Each fades in at its own
                                anchor somewhere across the frame — never from a single central
                                vanishing point — then grows and sharpens in place as it travels
                                toward the lens, before flying past and recycling to a fresh spot.
                            </p>

                            <p>
                                Twenty-one photographs circulate through fourteen positions, which
                                means seven frames are always resting off-stage, waiting to rotate
                                in. Nothing you see is a loop of the same arrangement. Let it run
                                and it keeps composing new ones.
                            </p>

                            <p>
                                Scroll to push the field forward faster; stop and it settles back
                                to its own steady glide. It pauses when it leaves your screen,
                                pauses in a hidden tab, and resolves to a single composed still if
                                you have asked your system to reduce motion.
                            </p>
                        </div>
                    </div>

                    <ul className='mt-24 grid gap-x-16 gap-y-14 border-t border-white/10 pt-16 sm:grid-cols-2'>
                        {PRINCIPLES.map((item, i) => (
                            <li key={item.title}>
                                <span className='font-mono text-[0.68rem] uppercase tracking-[0.14em] text-white/40'>
                                    {String(i + 1).padStart(2, '0')}
                                </span>

                                <h2 className='mt-4 font-serif text-2xl leading-[1.1] text-paper sm:text-[1.75rem]'>
                                    {item.title}
                                </h2>

                                <p className='mt-3 max-w-[42ch] text-[0.95rem] leading-[1.6] text-paper/65'>
                                    {item.body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </Layout>
    )
}

export default About
